import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Paper,
  Button,
  Typography,
  Box,
  Link,
} from '@mui/material';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import { makeStyles } from '@mui/styles';

import backgroundImage from 'assets/images/block_bg.png';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    margin: '0 auto',
    paddingTop: theme.spacing(4),
    maxWidth: '780px',
  },
  backButton: {
    marginLeft: 'auto',
    marginBottom: theme.spacing(2),
    width: 'fit-content',
    marginRight: theme.spacing(1),
    padding: theme.spacing(0, 1),
    borderColor: '#d2dae1',
    borderRadius: '50px',
    color: '#000',
    fontSize: '12px',
    backgroundColor: '#FFF',
    '&:hover': {
      borderColor: '#9fa5ab',
    }
  },
  paper: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2),
  },
  title: {
    position: 'absolute',
    top: '-35px',
    left: 0,
    right: 0,
    margin: 'auto',
    fontSize: '40px',
    fontWeight: 400,
    textAlign:'center',
    letterSpacing: '-2px',
  },
  coloredText: {
    color: '#006666',
  },
  subTitle: {
    fontSize: '29px',
    fontWeight: 100,
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(1),
    textAlign: 'center',
  },
  contentPaper: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    padding: '20px',
    gap: theme.spacing(2),
    borderRadius: '16px',
    backgroundImage: `url(${backgroundImage})`,
  },
  section: {
    background: 'rgba(174 184 186 / 0.8)',
    padding: theme.spacing(2),
  },
  sectionTitle: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    fontSize: '18px',
    fontWeight: 600,
  },
  sectionDescription: {
    marginBottom: theme.spacing(1),
  },
  link: {
    fontSize: '18px',
  }
}));

const Tutorials = () => {
  const classes = useStyles();
  const navigate = useNavigate();

  return (
    <div className={classes.root}>
      <Button variant='outlined' className={classes.backButton} onClick={() => navigate('/sets')}>
        <NavigateBeforeIcon />
        Sets
      </Button>
      <Paper className={classes.paper}>
        <Typography className={classes.title}>
          Fluency
          <span className={classes.coloredText}>{` FlashCards`}</span>
        </Typography>
        <Typography className={classes.subTitle}>
          instructional videos
        </Typography>
        <Paper
          elevation={0}
          className={classes.contentPaper}
        >
          <Paper
            elevation={0}
            className={classes.section}
          >
            <Typography className={classes.sectionTitle}>
              Introduction
            </Typography>
            <Typography className={classes.sectionDescription}>
              The video introduces you to the process of using the Fluency Flashcards application to view flashcards individually and together in timings and untimed sessions.
            </Typography>
            <Box
              component='div'
              sx={{
                height: '400px',
                backgroundImage: `url('https://universe-files.vzaar.com/vzaar/vzd/6ea/target/vzd6ea46bcaef2431382e09825888b1969.jpg')`,
                backgroundPosition: 'center',
                border: 'solid 3px #FFF',
                cursor: 'pointer',
              }}
            />
          </Paper>
          <Paper
            elevation={0}
            className={classes.section}
          >
            <Typography className={classes.sectionTitle}>
              Two Player Sessions
            </Typography>
            <Typography className={classes.sectionDescription}>
              In this video you will learn how to conduct two-player sesssions in the FFC, as either the performer or the reviewer.
            </Typography>
            <Box
              component='div'
              sx={{
                height: '400px',
                backgroundImage: `url('https://universe-files.vzaar.com/vzaar/vz8/1bb/target/vz81bb311d53f6469ab67bc034060aa022.jpg')`,
                backgroundPosition: 'center',
                border: 'solid 3px #FFF',
                cursor: 'pointer',
              }}
            />
          </Paper>
          <Paper
            elevation={0}
            className={classes.section}
          >
            <Typography className={classes.sectionTitle}>
              Charting
            </Typography>
            <Typography className={classes.sectionDescription}>
              This video covers the Daily and Timings charts and how to use them.
            </Typography>
            <Box
              component='div'
              sx={{
                height: '400px',
                backgroundImage: `url('https://universe-files.vzaar.com/vzaar/vz5/1c5/target/vz51c57798274b4ecda960fdc95e1d6059.jpg')`,
                backgroundPosition: 'center',
                border: 'solid 3px #FFF',
                cursor: 'pointer',
              }}
            />
          </Paper>
          <Paper
            elevation={0}
            className={classes.section}
          >
            <Typography className={classes.sectionTitle}>
              Creating Sets
            </Typography>
            <Typography className={classes.sectionDescription}>
              This video is for developers and covers the process of creating flashcard sets in the FFC.
            </Typography>
            <Box
              component='div'
              sx={{
                height: '400px',
                backgroundImage: `url('https://universe-files.vzaar.com/vzaar/vz5/1c5/target/vz51c57798274b4ecda960fdc95e1d6059.jpg')`,
                backgroundPosition: 'center',
                border: 'solid 3px #FFF',
                cursor: 'pointer',
              }}
            />
          </Paper>
          <Paper
            elevation={0}
            className={classes.section}
          >
            <Typography className={classes.sectionTitle}>
              Developing Effective Fluency Flashcards
            </Typography>
            <Box component='div' display='flex' gap={'10px'} alignItems='center' mb={2}>
              <Box
                component='div'
                sx={{
                  width: '100%',
                  height: '125px',
                  backgroundImage: `url('	https://fluencyflashcards.com/images/ffca-tm-screenshot.png')`,
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: 'cover',
                  border: 'solid 3px #FFF',
                  cursor: 'pointer',
                }}
              />
              <Typography>
                Dr. Kent Johnson details how to write effective flashcards for illustrating different kinds of learning, such as facts, concepts and principles, how to sequence cards, and how to conduct effective practice timings.
              </Typography>
            </Box>
            <Link href='https://elearningstoreinc.com/?p=74' underline='none' className={classes.link}>
              <strong>Free access on the Elearning Store</strong>
            </Link>
          </Paper>
        </Paper>
        <Button
          variant='outlined'
          className={classes.backButton}
          sx={{
            marginLeft: 'auto',
            marginRight: 'auto !important',
            marginTop: '16px',
          }}
          onClick={() => navigate('/sets')}
        >
          <NavigateBeforeIcon />
          Sets List
        </Button>
      </Paper>
    </div>
  );
};

export default Tutorials;
